/**
 * Grand format d'un secret (« Large Type » de 1Password, mots de passe colores de Bitwarden) : pour recopier un mot
 * de passe a la main sur un autre appareil (televiseur, console, poste sans le coffre), chaque caractere est montre
 * dans sa case, numerote, avec sa classe (majuscule, minuscule, chiffre, symbole) et un nom lu a voix haute.
 *
 * Pur, zero E/S : la valeur est lue dans l'entree au moment du rendu et n'est gardee nulle part.
 *
 * Deux pieges que la recopie a la main fait payer, et que ce module rend visibles :
 * - les sosies (l, I, 1, |, O, 0, o) : la classe et le nom les separent ;
 * - les caracteres invisibles (espace insecable, espace de largeur nulle, tabulation, retour a la ligne) qu'un
 *   copier-coller laisse dans un mot de passe : montres par leur point de code, jamais comme un vide.
 */
import type { EntrySecrets } from '@klavox/crypto-core';
import { readCustomFields } from './custom-fields.js';
import { entryFieldValue } from './entry-copy.js';
import { readItemHistory } from './item-history.js';
import { formatTimestamp } from '../ui/render-utils.js';
import { TYPED_FIELD_GROUPS, fieldSpecs, type TypedFieldGroup } from './typed-fields.js';

export type LargeTypeKind = 'upper' | 'lower' | 'letter' | 'digit' | 'space' | 'symbol' | 'invisible';

export interface LargeTypeCell {
  /** Rang du caractere, a partir de 1 (celui qu'on dicte : « le 7e est un tiret »). */
  readonly position: number;
  /** Ce qui est dessine dans la case : le caractere, ou un repere pour ce qui ne se voit pas. */
  readonly glyph: string;
  readonly kind: LargeTypeKind;
  /** Nom lu par un lecteur d'ecran, et affiche au survol. */
  readonly spoken: string;
}

const SYMBOL_NAMES: Readonly<Record<string, string>> = {
  '!': 'point d’exclamation',
  '"': 'guillemet droit',
  '#': 'diese',
  $: 'dollar',
  '%': 'pourcent',
  '&': 'esperluette',
  "'": 'apostrophe droite',
  '(': 'parenthese ouvrante',
  ')': 'parenthese fermante',
  '*': 'asterisque',
  '+': 'plus',
  ',': 'virgule',
  '-': 'tiret',
  '.': 'point',
  '/': 'barre oblique',
  ':': 'deux-points',
  ';': 'point-virgule',
  '<': 'inferieur',
  '=': 'egal',
  '>': 'superieur',
  '?': 'point d’interrogation',
  '@': 'arobase',
  '[': 'crochet ouvrant',
  '\\': 'barre oblique inversee',
  ']': 'crochet fermant',
  '^': 'accent circonflexe',
  _: 'tiret bas',
  '`': 'accent grave',
  '{': 'accolade ouvrante',
  '|': 'barre verticale',
  '}': 'accolade fermante',
  '~': 'tilde',
  '€': 'euro',
  '£': 'livre',
  '§': 'paragraphe',
  '°': 'degre',
};

/** Les invisibles qu'on sait nommer ; les autres sont dits par leur seul point de code. */
const INVISIBLE_NAMES: Readonly<Record<string, string>> = {
  '\t': 'tabulation',
  '\n': 'retour a la ligne',
  '\r': 'retour chariot',
  ' ': 'espace insecable',
  ' ': 'espace fine insecable',
  '​': 'espace de largeur nulle',
  '‌': 'antiliant de largeur nulle',
  '‍': 'liant de largeur nulle',
  '⁠': 'gluon de mot',
  '﻿': 'indicateur d’ordre des octets',
  '­': 'trait d’union conditionnel',
};

const segmenter: Intl.Segmenter | null = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter('fr', { granularity: 'grapheme' }) : null;

/** Coupe en caracteres percus (graphemes) : un emoji compose ou une lettre accentuee decomposee tient dans UNE case. */
function graphemes(value: string): string[] {
  if (segmenter === null) return Array.from(value);
  return Array.from(segmenter.segment(value), (part) => part.segment);
}

function codePoints(grapheme: string): string {
  return Array.from(grapheme, (char) => `U+${char.codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0')}`).join(' ');
}

function classify(grapheme: string): LargeTypeKind {
  if (grapheme === ' ') return 'space';
  // Espaces autres que l'espace ordinaire, controles, formats (largeur nulle, BOM), separateurs : rien ne se dessine.
  if (/^[\p{White_Space}\p{Cc}\p{Cf}\p{Zl}\p{Zp}]+$/u.test(grapheme)) return 'invisible';
  if (/^\p{Lu}\p{M}*$/u.test(grapheme)) return 'upper';
  if (/^\p{Ll}\p{M}*$/u.test(grapheme)) return 'lower';
  if (/^\p{Nd}$/u.test(grapheme)) return 'digit';
  if (/^\p{L}\p{M}*$/u.test(grapheme)) return 'letter';
  return 'symbol';
}

function spokenName(grapheme: string, kind: LargeTypeKind): string {
  switch (kind) {
    case 'upper':
      return `majuscule ${grapheme}`;
    case 'lower':
      return `minuscule ${grapheme}`;
    case 'digit':
      return `chiffre ${grapheme}`;
    case 'letter':
      return `lettre ${grapheme}`;
    case 'space':
      return 'espace';
    case 'invisible': {
      const name = INVISIBLE_NAMES[grapheme];
      return name === undefined ? `caractere invisible ${codePoints(grapheme)}` : `${name} (${codePoints(grapheme)})`;
    }
    case 'symbol': {
      const name = Object.hasOwn(SYMBOL_NAMES, grapheme) ? SYMBOL_NAMES[grapheme] : undefined;
      return name === undefined ? `symbole ${grapheme}` : name;
    }
  }
}

/** Les cases du grand format, dans l'ordre de la saisie. */
export function largeTypeCells(value: string): LargeTypeCell[] {
  return graphemes(value).map((grapheme, index) => {
    const kind = classify(grapheme);
    const glyph = kind === 'space' ? '␣' : kind === 'invisible' ? codePoints(grapheme) : grapheme;
    return { position: index + 1, glyph, kind, spoken: spokenName(grapheme, kind) };
  });
}

/** Resume a lire avant les cases (« 16 caracteres : 4 majuscules, ... ») ; les classes absentes ne sont pas citees. */
export function largeTypeSummary(cells: readonly LargeTypeCell[]): string {
  const count = (kind: LargeTypeKind): number => cells.filter((cell) => cell.kind === kind).length;
  const parts: string[] = [];
  const add = (n: number, singular: string, plural: string): void => {
    if (n > 0) parts.push(`${n} ${n === 1 ? singular : plural}`);
  };
  add(count('upper'), 'majuscule', 'majuscules');
  add(count('lower'), 'minuscule', 'minuscules');
  add(count('letter'), 'autre lettre', 'autres lettres');
  add(count('digit'), 'chiffre', 'chiffres');
  add(count('symbol'), 'symbole', 'symboles');
  add(count('space'), 'espace', 'espaces');
  add(count('invisible'), 'caractere invisible', 'caracteres invisibles');
  const total = `${cells.length} ${cells.length === 1 ? 'caractere' : 'caracteres'}`;
  return parts.length === 0 ? total : `${total} : ${parts.join(', ')}`;
}

/**
 * Les champs qui s'ouvrent en grand format, et le nom qu'on leur donne dans le titre : ce qu'on recopie a la main
 * caractere par caractere. Le mot de passe ; chaque champ type MASQUE tenant sur une ligne (numero de carte, CVV, PIN,
 * IBAN, phrase de passe BIP39, mot de passe de console ou SMTP…) ; chaque champ personnalise ; chaque ancien mot de
 * passe de l'historique (« Ancien mot de passe 2 », dans l'ordre affiche, le plus recent d'abord) ; chacun de ces champs
 * lu dans une version precedente (`version:<i>:<champ>`, nomme avec la date de la version). Pas un bloc multiligne
 * (phrase de recuperation, cle de licence, remarques medicales : ce sont des textes, pas des secrets a dicter), ni un
 * champ affiche en clair. Reference au format de `entryFieldValue` ; `null` = pas de grand format (champ vide, forge,
 * hors liste) -- c'est la garde du grand format, au bouton comme a l'ouverture.
 */
export function largeTypeFieldLabel(secrets: EntrySecrets, ref: string): string | null {
  if (entryFieldValue(secrets, ref) === null) return null;
  if (ref === 'password') return 'Mot de passe';
  const [kind, first, second] = ref.split(':');
  // `entryFieldValue` a deja refuse un rang forge (`01`, `-1`, `1e2`) ou hors de l'historique.
  if (kind === 'history' && first !== undefined) return `Ancien mot de passe ${Number(first) + 1}`;
  if (kind === 'version' && first !== undefined) {
    // Le champ de la version passe par la meme garde que celui de la fiche ; la date dit de quelle version il vient.
    // `readItemHistory` retire historiques et versions de chaque version : une reference imbriquee n'y trouve rien.
    const version = readItemHistory(secrets)[Number(first)];
    const label = version === undefined ? null : largeTypeFieldLabel(version.secrets, ref.slice(`version:${first}:`.length));
    return label === null ? null : `${label} (version du ${formatTimestamp(version!.replacedAt)})`;
  }
  if (kind === 'custom' && first !== undefined) return readCustomFields(secrets)[Number(first)]?.name ?? null;
  if (kind === 'typed' && first !== undefined && second !== undefined) {
    if (!(TYPED_FIELD_GROUPS as readonly string[]).includes(first)) return null;
    const spec = fieldSpecs(first as TypedFieldGroup).find((candidate) => candidate.key === second);
    return spec !== undefined && spec.sensitive === true && spec.multiline !== true && spec.options === undefined ? spec.label : null;
  }
  return null;
}
